import React, { Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Theme,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { formatDate } from 'utils/format';
import { SupplierRequestStatus } from 'models/supplier-request';

const STATUS_COLOR: Record<SupplierRequestStatus, 'warning' | 'success' | 'default'> = {
    [SupplierRequestStatus.PENDING]: 'warning',
    [SupplierRequestStatus.APPROVED]: 'success',
    [SupplierRequestStatus.DECLINED]: 'default',
};

/** DD-3702: what a row needs — supplier requests and lab ↔ clinic requests alike. */
type RequestRow = {
    _id: string;
    status: string;
    message?: string | null;
    requestedAt: string;
    requestedByName?: string | null;
    /** DD-3707: shown under the Declined chip when the decliner gave one. */
    declineReason?: string | null;
};

type RequestParty = { name?: string | null; organizationNumber?: string | null; city?: string | null };

type SupplierRequestTableProps<R extends RequestRow> = {
    requests: R[];
    /** Which organization to show on each row: the one asking (incoming) or the one asked. */
    labOf: (request: R) => RequestParty;
    /** Actions for a pending row (incoming side); the status chip otherwise. */
    renderActions?: (request: R) => ReactNode;
    /** The other party's column heading; "Lab" when not given. */
    partyLabelKey?: string;
    dataTest: string;
};

/** DD-3662: requests, newest first, with the other organization, the note and the status. */
const SupplierRequestTable = <R extends RequestRow>({
    requests,
    labOf,
    renderActions,
    partyLabelKey = 'client-directory:SUPPLIER_REQUEST_LAB',
    dataTest,
}: SupplierRequestTableProps<R>) => {
    const { t: translate } = useTranslation(['client-directory']);
    const isLargeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));
    return (
        // DD-3674: white paper and screen-size row height, like the other Denthub tables.
        <TableContainer component={Paper}>
            <Table size={isLargeScreen ? 'medium' : 'small'} data-test={dataTest}>
                <TableHead>
                    <TableRow>
                        <TableCell>{translate(partyLabelKey)}</TableCell>
                        <TableCell>{translate('client-directory:SUPPLIER_REQUEST_MESSAGE')}</TableCell>
                        <TableCell>{translate('client-directory:SUPPLIER_REQUEST_REQUESTED')}</TableCell>
                        <TableCell align="right">{translate('client-directory:SUPPLIER_REQUEST_STATUS')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {requests.map((request) => {
                        const lab = labOf(request);
                        const isPending = request.status === SupplierRequestStatus.PENDING;
                        return (
                            <TableRow key={request._id} data-test={`${dataTest}.row-${request._id}`}>
                                <TableCell>
                                    <Typography variant="body2" fontWeight={600}>
                                        {lab.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {[lab.organizationNumber, lab.city].filter(Boolean).join(' · ')}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ maxWidth: 360, whiteSpace: 'pre-wrap' }}>
                                    {request.message || '–'}
                                </TableCell>
                                <TableCell>
                                    <Box>{formatDate(request.requestedAt, 'date')}</Box>
                                    {request.requestedByName ? (
                                        <Typography variant="body2" color="text.secondary">
                                            {request.requestedByName}
                                        </Typography>
                                    ) : undefined}
                                </TableCell>
                                <TableCell align="right">
                                    {isPending && renderActions ? (
                                        renderActions(request)
                                    ) : (
                                        <Fragment>
                                            <Chip
                                                size="small"
                                                color={STATUS_COLOR[request.status as SupplierRequestStatus]}
                                                label={translate(
                                                    `client-directory:SUPPLIER_REQUEST_STATUS_${request.status}`,
                                                )}
                                            />
                                            {request.status === SupplierRequestStatus.DECLINED &&
                                            request.declineReason ? (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ maxWidth: 360, whiteSpace: 'pre-wrap', mt: 0.5 }}
                                                >
                                                    {`${translate('client-directory:DECLINE_REASON')}: ${
                                                        request.declineReason
                                                    }`}
                                                </Typography>
                                            ) : undefined}
                                        </Fragment>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default SupplierRequestTable;
